import { useMemo } from 'react';
import { Video, isShortVideo } from '../../lib/services/videoService';
import type { RowSize } from '../components/DashboardRow';

export interface DashboardRowDef {
  id: string;
  title: string;
  size: RowSize;
  videos: Video[];
}

// ── Row template names (cycled repeatedly until all videos are placed) ───
const ROW_NAMES = [
  'Trending Worldwide',
  'From Your Creators',
  'Recommended For You',
  'Popular This Week',
  'Fresh Uploads',
  'Hidden Gems',
  'Trending Now',
  'Because You Watched',
  'Watch Again',
];

// ── Helpers ──────────────────────────────────────────────────────────────

/** Fisher-Yates shuffle (pure — returns new array) */
function shuffle<T>(arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** Pick a row size that obeys repetition rules */
function pickSize(prevSizes: RowSize[], prevTitle: string | null, currentTitle: string): RowSize {
  const allSizes: RowSize[] = ['L', 'M', 'S'];
  const lastSize = prevSizes.length > 0 ? prevSizes[prevSizes.length - 1] : null;
  const secondLastSize = prevSizes.length > 1 ? prevSizes[prevSizes.length - 2] : null;

  let candidates = [...allSizes];

  // No 3 consecutive same-size rows
  if (lastSize && secondLastSize && lastSize === secondLastSize) {
    candidates = candidates.filter(s => s !== lastSize);
  }

  // If same title as previous row (consecutive repeat), force different size
  if (prevTitle === currentTitle && lastSize) {
    candidates = candidates.filter(s => s !== lastSize);
  }

  if (candidates.length === 0) candidates = allSizes;
  return candidates[Math.floor(Math.random() * candidates.length)];
}

const SIZE_COUNTS: Record<RowSize, number> = { L: 4, M: 3, S: 2 };

// ── Main hook ────────────────────────────────────────────────────────────

/**
 * Distributes ALL feed videos across repeating row names.
 * - Row templates cycle so every video gets placed in a row
 * - Rows use varied sizes (L=4, M=3, S=2)
 * - No 3 consecutive same-size rows
 * - Consecutive same-title rows must change size
 * - Row order shuffled per session
 */
export function useDashboardRows(
  feedVideos: Video[],
  trendingVideos: Video[],
): DashboardRowDef[] {
  return useMemo(() => {
    // Filter out shorts — they have their own dedicated row
    const nonShorts = feedVideos.filter(v => !isShortVideo(v));
    if (nonShorts.length === 0) return [];

    // Shuffle the video pool for variety
    const pool = shuffle(nonShorts);

    // Shuffle row names for session-based variety
    const shuffledNames = shuffle(ROW_NAMES);

    const rows: DashboardRowDef[] = [];
    const prevSizes: RowSize[] = [];
    let cursor = 0;
    let nameIndex = 0;

    // Keep creating rows until ALL videos are distributed
    while (cursor < pool.length) {
      // Cycle through shuffled row names
      const title = shuffledNames[nameIndex % shuffledNames.length];
      nameIndex++;

      const prevTitle = rows.length > 0 ? rows[rows.length - 1].title : null;
      const size = pickSize(prevSizes, prevTitle, title);
      const count = SIZE_COUNTS[size];

      // Slice videos from pool
      const rowVideos = pool.slice(cursor, cursor + count);
      cursor += rowVideos.length;

      if (rowVideos.length === 0) break;

      prevSizes.push(size);
      rows.push({
        id: `row-${title.toLowerCase().replace(/\s+/g, '-')}-${rows.length}`,
        title,
        size,
        videos: rowVideos,
      });
    }

    return rows;
  }, [feedVideos, trendingVideos]);
}
